/**
 * @file: ChatConversationHeader.tsx
 *
 * The conversation's in-pane header (desktop) — title + an inline status
 * label (typing or 在线/离线) after the name + the `⋮` room-info action. The
 * status sits AFTER the name (not on a second line) so it stays aligned with
 * the name. Pure: it renders exactly what it's given.
 * `WiredChatConversationHeader` composes the title/status from the room's
 * typing + member streams.
 */

import { Typography } from '@euler/app/components/typography';
import { PresenceDot } from '@euler/app/flows/chat/components/PresenceDot';
import { PaneHeader } from '@euler/components/PaneHeader';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MoreVertical } from 'lucide-react-native';
import { FC, memo } from 'react';
import { TouchableOpacity } from 'react-native';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export const ChatConversationHeader: FC<{
  title: string;
  /** Inline status after the name (typing or 在线/离线). */
  status?: string;
  /** Private chat with the peer online → show the dot next to the name. */
  online?: boolean;
  /** Omitted when the room is closed-for-me (removed/dismissed) — hides the
   * `⋮`, since the room detail isn't reachable then. */
  onOpenRoomInfo?: () => void;
}> = memo(({ title, status, online, onOpenRoomInfo }) => {
  const { rpx } = useResponsiveContext();
  return (
    <PaneHeader
      title={title}
      titleAccessory={online ? <PresenceDot /> : undefined}
      titleSuffix={
        status ? (
          <Typography.Text size={rpx(12)} color="#9ca3af">
            {status}
          </Typography.Text>
        ) : undefined
      }
      right={
        onOpenRoomInfo ? (
          <TouchableOpacity
            onPress={onOpenRoomInfo}
            hitSlop={kHitSlop}
            style={{ padding: rpx(4) }}
          >
            <MoreVertical size={rpx(20)} color="#111" />
          </TouchableOpacity>
        ) : undefined
      }
    />
  );
});
